<template>
  <div class="workforce-forecast-addon">
    <div class="addon-head">
      <div>
        <span>HRM_WORKFORCE_FORECAST</span>
        <h2>排班缺口与人力预测</h2>
        <p>基于部门人数、考勤样本和已审批请假，预测部门人力缺口、供需压力和未来排班风险。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">部门人力缺口预测</div>
          <el-table :data="overview.capacityGapRanking" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="staff_count" label="在职" width="70" />
            <el-table-column prop="expected_demand" label="预测需求" width="90" />
            <el-table-column prop="available_capacity" label="可用人力" width="90" />
            <el-table-column prop="forecast_gap" label="缺口" width="80" />
            <el-table-column prop="forecast_health" label="健康分" width="90" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">供需矩阵</div>
          <div class="status-list">
            <div v-for="item in overview.supplyDemandMatrix" :key="item.matrix_name" class="status-item">
              <span>{{ item.matrix_name }}</span>
              <strong>{{ item.dept_count }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">高风险部门</div>
          <el-table :data="overview.capacityRiskDeptList" size="small" v-loading="loading" empty-text="暂无高风险部门">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="risk_score" label="风险分" width="90" />
            <el-table-column prop="leave_pressure" label="请假压力%" width="100" />
            <el-table-column prop="abnormal_rate" label="异常率%" width="90" />
            <el-table-column prop="attendance_samples" label="样本" width="80" />
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">风险信号分布</div>
          <div class="status-list">
            <div v-for="item in overview.riskSignalDistribution" :key="item.signal_name" class="status-item">
              <span>{{ item.signal_name }}</span>
              <strong>{{ item.signal_count }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="24">
        <el-card shadow="never">
          <div slot="header">月度产能健康趋势</div>
          <div class="trend-list">
            <div v-for="item in overview.monthlyCapacityTrend" :key="item.month_label" class="trend-item">
              <span>{{ item.month_label }}</span>
              <strong>{{ item.capacity_health }}%</strong>
              <em>样本 {{ item.attendance_samples }} / 正常 {{ item.normal_samples }} / 风险 {{ item.risk_samples }}</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getWorkforceForecastOverview } from '../api/workforceForecast'

export default {
  name: 'WorkforceForecastOverview',
  data () {
    return {
      loading: false,
      overview: {
        activeStaffCount: 0,
        deptCount: 0,
        avgDailyAttendanceDemand: 0,
        approvedLeaveDays: 0,
        capacityGapRanking: [],
        capacityRiskDeptList: [],
        supplyDemandMatrix: [],
        riskSignalDistribution: [],
        monthlyCapacityTrend: []
      }
    }
  },
  computed: {
    metrics () {
      const gapCount = (this.overview.capacityGapRanking || []).filter(item => Number(item.forecast_gap) > 0).length
      return [
        { label: '在职人数', value: this.overview.activeStaffCount, meta: '当前可用编制基数' },
        { label: '部门数量', value: this.overview.deptCount, meta: `缺口部门 ${gapCount}`, tone: 'blue' },
        { label: '日均考勤需求', value: this.overview.avgDailyAttendanceDemand || 0, meta: '按历史样本折算', tone: 'orange' },
        { label: '已批请假天数', value: this.overview.approvedLeaveDays || 0, meta: '影响可用人力', tone: 'red' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getWorkforceForecastOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '排班缺口与人力预测加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="less" scoped>
.workforce-forecast-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #075985;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.status-list,
.trend-list {
  display: grid;
  gap: 10px;
}

.trend-list {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.status-item,
.trend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.trend-item {
  align-items: flex-start;
  flex-direction: column;
}

.status-item span,
.trend-item span,
.trend-item em {
  color: #5f6f89;
  font-style: normal;
}

.status-item strong,
.trend-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
